import { IGetValueArgs, TGetValueFn, TInfoValue } from "@components/smart/FieldInfo";
import {
    CompanyVatStatusTemporalEntity,
    ICompanyEntity,
    ICompanyVatStatusTemporalEntity,
    IDocumentDraftEntity
} from "@odata/GeneratedEntityTypes";
import { DocumentTypeCode, VatStatusCode } from "@odata/GeneratedEnums";
import { groupAndOptimizeRanges, TTemporal } from "@odata/TemporalUtils";
import { isEuRuleExceptAgendaDomestic } from "@pages/admin/vatRules/VatRules.global.utils";
import { IDocumentExtendedEntity } from "@pages/documents/DocumentInterfaces";
import { isDDOP } from "@pages/documents/proformaInvoices/ProformaInvoice.global.utils";

import { IAppContext } from "../../contexts/appContext/AppContext.types";
import { FormStorage } from "../../views/formView/FormStorage";

export const IsOssModePath = "##IsOssMode##";

export const OssAvailableDocTypes = [
    DocumentTypeCode.InvoiceIssued, DocumentTypeCode.CorrectiveInvoiceIssued,
    DocumentTypeCode.OtherReceivable,
    // proforma, but DDOPP only
    DocumentTypeCode.ProformaInvoiceIssued
];

export const isOssCompanyForDate = (context: IAppContext, date: Date): boolean => {
    const company = context.getCompany();

    if (!company) {
        return false;
    }

    const bagOssOnly = groupAndOptimizeRanges(company.TemporalPropertyBag as TTemporal<ICompanyVatStatusTemporalEntity>[], [CompanyVatStatusTemporalEntity.IsOssMode]);

    return !!(bagOssOnly ?? []).find(bag => bag.DateValidFrom <= date && (!bag.DateValidTo || bag.DateValidTo >= date))?.IsOssMode;
};

export const hasEverBeenOrWillBeOssCompany = (context: IAppContext, companyEntity?: ICompanyEntity): boolean => {
    const company = companyEntity ?? context.getCompany();

    if (!company) {
        return false;
    }

    const temporalPropertyBag = company.TemporalPropertyBag ?? [];

    // visible, if any of the bags has IsOssMode, both in past or in future, so that the user can set the bank account
    return !!temporalPropertyBag.find(tpb => !!tpb.IsOssMode);
};

export const isOssAvailable = (storage: FormStorage, documentType: DocumentTypeCode): boolean => {
    if (documentType === DocumentTypeCode.ProformaInvoiceIssued && !isDDOP({ context: storage.context, storage })) {
        // in proforma, oss is only available for DDOP
        return false;
    }

    return OssAvailableDocTypes.includes(documentType);
};

export const isOssMode = (storage: FormStorage, date?: Date): boolean => {
    const document = storage.getEntity<IDocumentDraftEntity & IDocumentExtendedEntity>();

    if (document.DocumentTypeCode === DocumentTypeCode.CorrectiveInvoiceIssued) {
        // for corrective invoice, oss mode is only available if the corrected document is in oss mode
        return !!document[IsOssModePath];
    }

    // oss switch should be driven by DateTaxableSupply
    const dateTaxableSupply = date ?? document.DateTaxableSupply;

    return isOssAvailable(storage, document.DocumentTypeCode as DocumentTypeCode) && isOssCompanyForDate(storage.context, dateTaxableSupply)
            && isEuRuleExceptAgendaDomestic({
            storage,
            context: storage.context,
            bindingContext: storage.data.bindingContext
        }) && document?.BusinessPartner?.VatStatusCode === VatStatusCode.NotVATRegistered;
};

export const isOssDocument = (storage: FormStorage, date?: Date): boolean => {
    const document = storage.getEntity<IDocumentExtendedEntity>();

    return isOssMode(storage, date) && !!document[IsOssModePath];
};

export const isOssDocumentCallback: TGetValueFn<boolean> = (args: IGetValueArgs): boolean => {
    return isOssDocument(args.storage as FormStorage);
};

export const isOssModeCallback: TInfoValue<boolean> = (args: IGetValueArgs): boolean => {
    return isOssMode(args.storage as FormStorage);
};